import { Dispatch, useCallback } from 'react'
import { DiscountCode } from '../../../pure-js/types/DiscountCodeTypes'
import { DiscountPricingConfig } from '../../../pure-js/types/DiscountTypes'
import {
  Activity,
  DanceRoles,
  EnrollmentPaymentStatus,
  EnrollmentWithPayment,
  PayFor,
  PaymentMethod
} from '../../../pure-js/types/GrooverTypes'
import { GetUserPreviousDiscountsResponse } from '../../../pure-js/types/QueryParamTypes'
import { RegisterActions } from '../enums/RegisterSteps'
import { CourseRegistrationFormValues } from '../helpers/CourseRegistrationStepOneHelper'
import { EarlyBirdBookingData } from './useAppState'

export type RegisterActionFunctions = {
  onSaveRegistrationData: (data: CourseRegistrationFormValues) => void
  onResetRegistration: () => void
  onOpenStep: (step: number) => void
  onSetFinalPrice: (price: string) => void
  onCompleteRegistration: () => void
  onSetFormErrors: (hasFormErrors: boolean) => void
  onSetSelectedActivities: (selectedActivities: Activity[]) => void
  onSetIsMainUserStudent: (isMainUserStudent: boolean) => void
  onSetIsPartnerStudent: (isPartnerStudent: boolean) => void
  onSetIsMainUserSibling: (isMainUserSibling: boolean) => void
  onSetIsPartnerSibling: (isPartnerSibling: boolean) => void
  onSetIsMainUserSenior: (isMainUserSenior: boolean) => void
  onSetIsPartnerSenior: (isPartnerSenior: boolean) => void
  onSetEnrollmentId: (enrollmentId: string) => void
  onSetPaymentMethod: (paymentMethod: PaymentMethod) => void
  onSetIsPaid: (isPaid: boolean) => void
  onSetPaymentStatus: (paymentStatus: EnrollmentPaymentStatus) => void
  onSetIsRegistrationSubmitted: () => void
  onSetMainUserActivePeriods: (mainUserActivePeriods: string[]) => void
  onSetPartnerUserActivePeriods: (partnerUserActivePeriods: string[]) => void
  onSetMainUserPreviousDiscounts: (data: GetUserPreviousDiscountsResponse) => void
  onSetPartnerUserPreviousDiscounts: (data: GetUserPreviousDiscountsResponse) => void
  onSetObfuscatedEnrollment: (obfuscatedEnrollment: EnrollmentWithPayment) => void
  onSetMainUserSsn: (mainUserSsn: string) => void
  onSetPartnerUserSsn: (partnerUserSsn: string) => void
  onSetEnrollmentComment: (enrollmentComment: string) => void
  onSetEnrollmentDiscountCode: (code: DiscountCode) => void
  onSetEnrollmentRegisteredPartners: (partners: Record<string, string>) => void
  onSetPayFor: (payFor: PayFor) => void
  onSetDiscountType: (discountType: DiscountPricingConfig['registrationType']) => void
  onSetDiscountRegion: (discountRegion: string) => void
  onSetIndividualFlow: (individualFlow: boolean) => void
  onSetRole: (role: DanceRoles | null) => void
  onSetPartnerRole: (partnerRole: DanceRoles | null) => void
  onSetEarlyBirdBooking: (earlyBirdBooking: Record<string, EarlyBirdBookingData>) => void
  onSetSingleEventQuantity: (quantity: number) => void
  onSetChildrenInput: (childrenInput: Record<string, { name: string; birthYear: string }>) => void
}

export const useRegisterActionFunctions = (
  dispatcher: Dispatch<{ type: RegisterActions; payload: unknown }>
): RegisterActionFunctions => ({
  onSaveRegistrationData: useCallback(
    (data: CourseRegistrationFormValues) => dispatcher({ type: RegisterActions.SAVE_REGISTRATION_DATA, payload: data }),
    [dispatcher]
  ),
  onResetRegistration: useCallback(
    () => dispatcher({ type: RegisterActions.RESET_REGISTRATION, payload: true }),
    [dispatcher]
  ),
  onOpenStep: useCallback(
    (step: number) => dispatcher({ type: RegisterActions.SET_ACTIVE_STEP, payload: { activeRegisterStep: step } }),
    [dispatcher]
  ),
  onSetFinalPrice: useCallback(
    (price: string) => dispatcher({ type: RegisterActions.SET_FINAL_PRICE, payload: { finalPrice: price } }),
    [dispatcher]
  ),
  onCompleteRegistration: useCallback(
    () => dispatcher({ type: RegisterActions.SET_IS_REGISTRATION_COMPLETED, payload: true }),
    [dispatcher]
  ),
  onSetFormErrors: useCallback(
    (hasFormErrors: boolean) => dispatcher({ type: RegisterActions.SET_FORM_ERRORS, payload: { hasFormErrors } }),
    [dispatcher]
  ),
  onSetSelectedActivities: useCallback(
    (selectedActivities: Activity[]) =>
      dispatcher({ type: RegisterActions.SET_SELECTED_ACTIVITIES, payload: { selectedActivities } }),
    [dispatcher]
  ),
  onSetIsMainUserStudent: useCallback(
    (isMainUserStudent: boolean) =>
      dispatcher({ type: RegisterActions.SET_IS_MAIN_USER_STUDENT, payload: { isMainUserStudent } }),
    [dispatcher]
  ),
  onSetIsPartnerStudent: useCallback(
    (isPartnerStudent: boolean) =>
      dispatcher({ type: RegisterActions.SET_IS_PARTNER_STUDENT, payload: { isPartnerStudent } }),
    [dispatcher]
  ),
  onSetIsMainUserSibling: useCallback(
    (isMainUserSibling: boolean) =>
      dispatcher({ type: RegisterActions.SET_IS_MAIN_USER_SIBLING, payload: { isMainUserSibling } }),
    [dispatcher]
  ),
  onSetIsPartnerSibling: useCallback(
    (isPartnerSibling: boolean) =>
      dispatcher({ type: RegisterActions.SET_IS_PARTNER_SIBLING, payload: { isPartnerSibling } }),
    [dispatcher]
  ),
  onSetIsMainUserSenior: useCallback(
    (isMainUserSenior: boolean) =>
      dispatcher({ type: RegisterActions.SET_IS_MAIN_USER_SENIOR, payload: { isMainUserSenior } }),
    [dispatcher]
  ),
  onSetIsPartnerSenior: useCallback(
    (isPartnerSenior: boolean) =>
      dispatcher({ type: RegisterActions.SET_IS_PARTNER_SENIOR, payload: { isPartnerSenior } }),
    [dispatcher]
  ),
  onSetEnrollmentId: useCallback(
    (enrollmentId: string) => dispatcher({ type: RegisterActions.SET_ENROLLMENT_ID, payload: { enrollmentId } }),
    [dispatcher]
  ),
  onSetPaymentMethod: useCallback(
    (paymentMethod: PaymentMethod) =>
      dispatcher({ type: RegisterActions.SET_PAYMENT_METHOD, payload: { paymentMethod } }),
    [dispatcher]
  ),
  onSetIsPaid: useCallback(
    (isPaid: boolean) => dispatcher({ type: RegisterActions.SET_IS_PAID, payload: { isPaid } }),
    [dispatcher]
  ),
  onSetPaymentStatus: useCallback(
    (paymentStatus: EnrollmentPaymentStatus) =>
      dispatcher({ type: RegisterActions.SET_PAYMENT_STATUS, payload: { paymentStatus } }),
    [dispatcher]
  ),
  onSetIsRegistrationSubmitted: useCallback(
    () => dispatcher({ type: RegisterActions.SET_IS_REGISTRATION_SUBMITTED, payload: true }),
    [dispatcher]
  ),
  onSetMainUserActivePeriods: useCallback(
    (mainUserActivePeriods: string[]) =>
      dispatcher({ type: RegisterActions.SET_MAIN_USER_ACTIVE_PERIODS, payload: { mainUserActivePeriods } }),
    [dispatcher]
  ),
  onSetPartnerUserActivePeriods: useCallback(
    (partnerUserActivePeriods: string[]) =>
      dispatcher({ type: RegisterActions.SET_PARTNER_USER_ACTIVE_PERIODS, payload: { partnerUserActivePeriods } }),
    [dispatcher]
  ),
  onSetMainUserPreviousDiscounts: useCallback(
    (data: GetUserPreviousDiscountsResponse) =>
      dispatcher({ type: RegisterActions.SET_MAIN_USER_PREVIOUS_DISCOUNTS, payload: data }),
    [dispatcher]
  ),
  onSetPartnerUserPreviousDiscounts: useCallback(
    (data: GetUserPreviousDiscountsResponse) =>
      dispatcher({ type: RegisterActions.SET_PARTNER_USER_PREVIOUS_DISCOUNTS, payload: data }),
    [dispatcher]
  ),
  onSetObfuscatedEnrollment: useCallback(
    (obfuscatedEnrollment: EnrollmentWithPayment) =>
      dispatcher({ type: RegisterActions.SET_OBFUSCATED_ENROLLMENT, payload: { obfuscatedEnrollment } }),
    [dispatcher]
  ),
  onSetMainUserSsn: useCallback(
    (mainUserSsn: string) => dispatcher({ type: RegisterActions.SET_MAIN_USER_SSN, payload: { mainUserSsn } }),
    [dispatcher]
  ),
  onSetPartnerUserSsn: useCallback(
    (partnerUserSsn: string) => dispatcher({ type: RegisterActions.SET_PARTNER_USER_SSN, payload: { partnerUserSsn } }),
    [dispatcher]
  ),
  onSetEnrollmentComment: useCallback(
    (enrollmentComment: string) =>
      dispatcher({ type: RegisterActions.SET_ENROLLMENT_COMMENT, payload: { enrollmentComment } }),
    [dispatcher]
  ),
  onSetEnrollmentDiscountCode: useCallback(
    (code: DiscountCode) => dispatcher({ type: RegisterActions.SET_ENROLLMENT_DISCOUNT_CODE, payload: { code } }),
    [dispatcher]
  ),
  onSetEnrollmentRegisteredPartners: useCallback(
    (registeredPartners: Record<string, string>) =>
      dispatcher({ type: RegisterActions.SET_ENROLLMENT_REGISTERED_PARTNERS, payload: { registeredPartners } }),
    [dispatcher]
  ),
  onSetPayFor: useCallback(
    (payFor: PayFor) => dispatcher({ type: RegisterActions.SET_PAYFOR, payload: { payFor } }),
    [dispatcher]
  ),
  onSetDiscountType: useCallback(
    (discountType: string) => dispatcher({ type: RegisterActions.SET_DISCOUNT_TYPE, payload: { discountType } }),
    [dispatcher]
  ),
  onSetDiscountRegion: useCallback(
    (discountRegion: string) => dispatcher({ type: RegisterActions.SET_DISCOUNT_REGION, payload: { discountRegion } }),
    [dispatcher]
  ),
  onSetIndividualFlow: useCallback(
    (individualFlow: boolean) => dispatcher({ type: RegisterActions.SET_INDIVIDUAL_FLOW, payload: { individualFlow } }),
    [dispatcher]
  ),
  onSetRole: useCallback(
    (role: DanceRoles | null) => dispatcher({ type: RegisterActions.SET_ROLE, payload: { role } }),
    [dispatcher]
  ),
  onSetPartnerRole: useCallback(
    (partnerRole: DanceRoles | null) =>
      dispatcher({ type: RegisterActions.SET_PARTNER_ROLE, payload: { partnerRole } }),
    [dispatcher]
  ),
  onSetEarlyBirdBooking: useCallback(
    (earlyBirdBooking: Record<string, EarlyBirdBookingData>) =>
      dispatcher({ type: RegisterActions.SET_EARLY_BIRD_BOOKING, payload: { earlyBirdBooking } }),
    [dispatcher]
  ),
  onSetSingleEventQuantity: useCallback(
    (singleEventQuantity: number) =>
      dispatcher({ type: RegisterActions.SET_SINGLE_EVENT_QUANTITY, payload: { singleEventQuantity } }),
    [dispatcher]
  ),
  onSetChildrenInput: useCallback(
    (childrenInput: Record<string, { name: string; birthYear: string }>) =>
      dispatcher({ type: RegisterActions.SET_CHILDREN_INPUT, payload: { childrenInput } }),
    [dispatcher]
  )
})
